import React, { useEffect, useState } from 'react';
import LoginDetails from './blocks/LoginDetails';
import { useDispatch, useSelector } from 'react-redux';
import { countryList, GetPayerDetailService, UpdateUserDetails } from '../../services/ProfileService';
import { setPayer, setUser } from '../../store/actions';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import Select from 'react-select';
import {
    capitalize,
    languageListOptions,
    nationalityListOptions,
    disabilityListOption,
    provinceOptions,
    raceOptions,
    genderOptions,
    titleOptions,
} from '../../utils/commonFunction';
import moment from 'moment';
import { EMAIL_REGEX, MOBILE_REGEX } from '../../utils/Constants';
import ProfilePersonalDocuments from './blocks/ProfilePersonalDocuments';
import { Heading6, LabelMedium } from '../common/GlobalElements/Typography';
import { StandardButton } from '../common/GlobalElements/StandardButton';
import { ContainerWrapper } from '../common/GlobalElements/Wrappers';
import { StandardErrorMessage } from '../../utils/commonFunction';
import ToggleButton from '../library/ToggleButton';
import CopyableField from '../library/CopyableField';
import DisabledInput from '../library/DisabledInput';
import { CollapsibleSection, DoubleCollapsibleSection } from './blocks/ProfileCollapsibleSection';
import EmergencyContactFields, { emergencyContactValidationSchema } from './blocks/EmergencyContactFields';
import { useIsMobile } from '../../hooks/useIsMobile';
import styled from 'styled-components';
import { tokens } from '../../public/tokens';

const PersonalInformation = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [copyText, setCopyText] = useState(0);
    const [countries, setCountries] = useState([]);
    const [deliveryAddress, setDeliveryAddress] = useState(false);
    let details;
    let brand;

    let user = useSelector((state) => state.user);

    if (localStorage.getItem('user') != null) {
        details = JSON.parse(localStorage.getItem('user'));
    }

    if (localStorage.getItem('brandVariables') != null) {
        brand = JSON.parse(localStorage.getItem('brandVariables'));
    }

    const isMobile = useIsMobile();

    const copyField = (text) => {
        setCopyText(1);
        navigator.clipboard.writeText(text);
        setTimeout(() => {
            setCopyText(0);
        }, 1000);
    };

    const Required = styled.span({
        color: tokens.colors.states.alert[500],
        textAlign: 'center',
        fontFamily: tokens.globals['font-family'],
        fontSize: tokens.typography.fontSize['lg-14px'],
        fontWeight: tokens.typography.fontWeights.bold,
        lineHeight: '120%',
    });

    const getSelectStyles = () => ({
        control: () => ({
            border: `1px solid ${tokens.colors.ui.border}`,
            borderRadius: tokens.globals['border-radius'],
            backgroundColor: tokens.colors.neutral.white,
            display: 'flex',
            minHeight: '40px',
        }),
        singleValue: () => ({
            color: tokens.colors.neutral.grey[600],
            fontSize: tokens.typography.fontSize['base-16px'],
        }),
        placeholder: () => ({
            color: tokens.colors.neutral.grey[600],
            fontSize: tokens.typography.fontSize['base-16px'],
        }),
    });

    useEffect(() => {
        countryList()
            .then((res) => {
                const respData = res.data.data;
                const countryOpt = respData.map((item) => {
                    return { value: item.value, label: item.value };
                });
                setCountries([...countryOpt]);
            })
            .catch((error) => {
                setCountries([]);
            });
        GetPayerDetailService()
            .then((res) => {
                dispatch(setPayer(res.data[0]));
            })
            .catch((error) => {});
    }, []);

    const validationSchema = Yup.object({
        first_name: Yup.string().required('First Name is required'),
        last_name: Yup.string().required('Last Name is required'),
        title: Yup.string().required('Title is required'),
        number: Yup.string().required('ID Number is required'),
        bday_year: Yup.string().required('Year is required'),
        bday_month: Yup.string().required('Month is required'),
        bday_day: Yup.string().required('Day is required'),
        race: Yup.string().required('Ethnicity is required'),
        nationality: Yup.string().required('Nationality is required'),
        disability: Yup.string().required('Disablity is required'),
        gender: Yup.string().required('Gender is required'),
        language: Yup.string().required('Language is required'),
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required')
            .matches(EMAIL_REGEX, 'Invalid email address'),
        mobile: Yup.string()
            .matches(MOBILE_REGEX, 'Phone number should be 0-9 of 10 digits with country code')
            .required('Mobile is required'),
        phone: Yup.string().matches(MOBILE_REGEX, 'Phone number should be 0-9 of 10 digits with country code'),
        a_phone: Yup.string().matches(MOBILE_REGEX, 'Phone number should be 0-9 of 10 digits with country code'),
        ph_street: Yup.string()
            .required('Street address is required')
            .max(150, 'Street address must be 150 characters or less'),
        ph_city: Yup.string().required('City is required'),
        ph_province: Yup.string().required('Province is required'),
        ph_country: Yup.string().required('Country is required'),
        ph_zip: Yup.string().required('Postal Code is required'),
        post_street: Yup.string().max(150, 'Street address must be 150 characters or less'),
        ...emergencyContactValidationSchema,
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            first_name: user?.first_name ? user?.first_name : '',
            second_name: user?.second_name ? user?.second_name : '',
            third_name: user?.third_name ? user?.third_name : '',
            last_name: user?.last_name ? user?.last_name : '',
            title: user?.title ? user?.title : '',
            number: user?.number ? user?.number : '',
            bday_year: user?.bday_year ? user?.bday_year : '',
            bday_month: user?.bday_month ? user?.bday_month : '',
            bday_day: user?.bday_day ? user?.bday_day : '',
            race: user?.race ? user?.race : '',
            nationality: user?.nationality ? user?.nationality : '',
            disability: user?.disability ? user?.disability : '',
            gender: user?.gender ? user?.gender : '',
            language: user?.language ? user?.language : '',
            email: user?.email ? user?.email : '',
            mobile: user?.mobile ? user?.mobile : '',
            phone: user?.phone ? user?.phone : '',
            a_phone: user?.a_phone ? user?.a_phone : '',
            ph_street: user?.ph_street ? user?.ph_street : '',
            ph_city: user?.ph_city ? user?.ph_city : '',
            ph_province: user?.ph_province ? user?.ph_province : '',
            ph_country: user?.ph_country ? user?.ph_country : '',
            ph_zip: user?.ph_zip ? user?.ph_zip : '',
            post_street: user?.post_street ? user?.post_street : '',
            post_city: user?.post_city ? user?.post_city : '',
            post_province: user?.post_province ? user?.post_province : '',
            post_country: user?.post_country ? user?.post_country : '',
            post_zip: user?.post_zip ? user?.post_zip : '',
            emergency_contact_name_one: user?.emergency_contact_name_one ? user?.emergency_contact_name_one : '',
            emergency_contact_no_one: user?.emergency_contact_no_one ? user?.emergency_contact_no_one : '',
            emergency_contact_name_two: user?.emergency_contact_name_two ? user?.emergency_contact_name_two : '',
            emergency_contact_no_two: user?.emergency_contact_no_two ? user?.emergency_contact_no_two : '',
            medical_aid: user?.medical_aid ? user?.medical_aid : '',
            medical_aid_number: user?.medical_aid_number ? user?.medical_aid_number : '',
        },
        validationSchema,
        onSubmit: (values) => {
            setLoading(true);
            UpdateUserDetails(values)
                .then((res) => {
                    if (res.status == 200) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: 'Updated successfully',
                        }).then((result) => {
                            if (result.value) {
                                dispatch(setUser({ ...user, ...res.data }));
                            }
                        });
                    }
                })
                .catch((err) => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: err.response.data.message,
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
    });

    const { values, errors, setFieldValue, touched, handleChange, handleBlur, handleSubmit } = formik;

    return (
        <>
            <form onSubmit={handleSubmit}>
                <>
                    <CollapsibleSection title="Personal Information" storageKey="personalInformation" defaultOpen>
                        <div>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="form-group" title="First Name">
                                        <DisabledInput
                                            label="First Name"
                                            placeholder="As per ID/Passport"
                                            value={values.first_name}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="Second Name">
                                        <LabelMedium>Second Name</LabelMedium>
                                        <input
                                            type="text"
                                            className={'form-control'}
                                            placeholder="As per ID/Passport"
                                            value={values.second_name}
                                            name="second_name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="Third Name">
                                        <LabelMedium>Third Name</LabelMedium>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="As per ID/Passport"
                                            value={values.third_name}
                                            name="third_name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="form-group" title="Last Name">
                                        <DisabledInput
                                            label="Last Name"
                                            placeholder="As per ID/Passport"
                                            value={values.last_name}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="Title">
                                        <LabelMedium>
                                            Title<Required>*</Required>
                                        </LabelMedium>
                                        <div className="form-icon-group">
                                            <Select
                                                className={
                                                    'form-control custom-select-box' +
                                                    (errors.title && touched.title ? ' is-invalid' : '')
                                                }
                                                name="title"
                                                value={titleOptions.find((item) => item.value == values.title)}
                                                onChange={(value) => {
                                                    setFieldValue('title', value.value);
                                                }}
                                                onBlur={handleBlur}
                                                maxMenuHeight={175}
                                                placeholder={values.title ? capitalize(values.title) : 'Select Title'}
                                                options={titleOptions}
                                                styles={getSelectStyles()}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="form-group" title="Date of Birth">
                                        <DisabledInput
                                            label="Date of Birth"
                                            placeholder="DD/MM/YYYY"
                                            value={`${values.bday_day}/${values.bday_month}/${values.bday_year}`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group" title="Gender">
                                    <DisabledInput
                                        label="Gender"
                                        placeholder="As per ID/Passport"
                                        value={
                                            genderOptions?.find((item) => item.value === values?.gender)?.label || ''
                                        }
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group" title="Race">
                                    <DisabledInput
                                        label="Ethnicity"
                                        placeholder="Ethnicity"
                                        value={raceOptions?.find((item) => item.value === values?.race)?.label || ''}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group" title="Nationality">
                                    <DisabledInput
                                        label="Nationality"
                                        placeholder="As per ID/Passport"
                                        value={
                                            nationalityListOptions?.find((item) => item.value === values?.nationality)
                                                ?.label || ''
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group" title="ID/Passport Number">
                                    <DisabledInput
                                        label="ID/Passport Number"
                                        placeholder="As per ID/Passport"
                                        value={values.number}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group" title="Home Language">
                                    <LabelMedium>Language </LabelMedium>
                                    <div className="form-icon-group">
                                        <Select
                                            className={
                                                'form-control custom-select-box' +
                                                (errors.language && touched.language ? ' is-invalid' : '')
                                            }
                                            name="language"
                                            value={languageListOptions.find(
                                                (item) => item.value.toLowerCase() == values.language.toLowerCase(),
                                            )}
                                            onChange={(value) => {
                                                setFieldValue('language', value.value);
                                            }}
                                            onBlur={handleBlur}
                                            maxMenuHeight={175}
                                            placeholder={'Select Language'}
                                            options={languageListOptions}
                                            styles={getSelectStyles()}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group" title="Disability">
                                    <DisabledInput
                                        label="Disability"
                                        placeholder="No Disability Specified"
                                        value={
                                            disabilityListOption?.find((item) => item.value === values?.disability)
                                                ?.label || ''
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </CollapsibleSection>
                </>
                <DoubleCollapsibleSection
                    title="Address and Contact"
                    storageKey="addressAndContact"
                    defaultOpen
                    secondaryTitle="Delivery Address"
                    showSecondary={deliveryAddress}
                    toggle={
                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <LabelMedium className="mb-0">Different Delivery Address</LabelMedium>
                                <i
                                    className="fal fa-info-circle"
                                    title="Toggle this on if your delivery address differs from your physical address."
                                ></i>
                            </div>
                            <ToggleButton
                                style={{ marginTop: '6px' }}
                                id="delivery_address"
                                role="switch"
                                size="md"
                                checked={deliveryAddress}
                                onChange={(e) => setDeliveryAddress(e.target.checked)}
                            />
                        </div>
                    }
                    secondaryContent={
                        <>
                            <div className="row">
                                <div className="col-md-8">
                                    <div className="form-group" title="Street Address">
                                        <LabelMedium>
                                            Physical Address<Required>*</Required>
                                        </LabelMedium>
                                        <input
                                            type="text"
                                            className={
                                                'form-control' +
                                                (errors.post_street && touched.post_street ? ' is-invalid' : '')
                                            }
                                            name="post_street"
                                            placeholder="Street Number and Name"
                                            value={values.post_street}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="City">
                                        {!isMobile && <LabelMedium>&nbsp;</LabelMedium>}
                                        <input
                                            type="text"
                                            className={
                                                'form-control' +
                                                (errors.post_city && touched.post_city ? ' is-invalid' : '')
                                            }
                                            name="post_city"
                                            placeholder="City"
                                            value={values.post_city}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="form-group" title="Province">
                                        <div className="form-icon-group">
                                            <Select
                                                className={
                                                    'form-control custom-select-box' +
                                                    (errors.post_province && touched.post_province ? ' is-invalid' : '')
                                                }
                                                name="post_province"
                                                value={provinceOptions.find(
                                                    (item) => item.value == values.post_province,
                                                )}
                                                onChange={(value) => setFieldValue('post_province', value.value)}
                                                onBlur={handleBlur}
                                                options={provinceOptions}
                                                maxMenuHeight={175}
                                                placeholder={'Province'}
                                                styles={getSelectStyles()}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="Country">
                                        <div className="form-icon-group">
                                            <Select
                                                className={
                                                    'form-control custom-select-box' +
                                                    (errors.post_country && touched.post_country ? ' is-invalid' : '')
                                                }
                                                name="post_country"
                                                value={countries.find((item) => item.value == values.post_country)}
                                                onChange={(value) => setFieldValue('post_country', value.value)}
                                                onBlur={handleBlur}
                                                maxMenuHeight={175}
                                                placeholder={'Country'}
                                                options={countries}
                                                styles={getSelectStyles()}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="form-group" title="Postal Code">
                                        <input
                                            type="text"
                                            className={
                                                'form-control' +
                                                (errors.post_zip && touched.post_zip ? ' is-invalid' : '')
                                            }
                                            name="post_zip"
                                            value={values.post_zip}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            placeholder={'PostalCode'}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    }
                >
                    <div className="row">
                        <div className="col-md-8">
                            <div className="form-group" title="Street Address">
                                <LabelMedium>
                                    Physical Address<Required>*</Required>
                                </LabelMedium>
                                <input
                                    type="text"
                                    className={
                                        'form-control' + (errors.ph_street && touched.ph_street ? ' is-invalid' : '')
                                    }
                                    name="ph_street"
                                    placeholder="Street Number and Name"
                                    value={values.ph_street}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group" title="City">
                                {!isMobile && <LabelMedium>&nbsp;</LabelMedium>}
                                <input
                                    type="text"
                                    className={
                                        'form-control' + (errors.ph_city && touched.ph_city ? ' is-invalid' : '')
                                    }
                                    name="ph_city"
                                    placeholder="City"
                                    value={values.ph_city}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group" title="Province">
                                <div className="form-icon-group">
                                    <Select
                                        className={
                                            'form-control custom-select-box' +
                                            (errors.ph_province && touched.ph_province ? ' is-invalid' : '')
                                        }
                                        name="ph_province"
                                        value={provinceOptions.find((item) => item.value == values.ph_province)}
                                        onChange={(value) => setFieldValue('ph_province', value.value)}
                                        onBlur={handleBlur}
                                        options={provinceOptions}
                                        maxMenuHeight={175}
                                        placeholder={'Province'}
                                        styles={getSelectStyles()}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group" title="Country">
                                <div className="form-icon-group">
                                    <Select
                                        className={
                                            'form-control custom-select-box' +
                                            (errors.ph_country && touched.ph_country ? ' is-invalid' : '')
                                        }
                                        name="ph_country"
                                        value={countries.find((item) => item.value == values.ph_country)}
                                        onChange={(value) => setFieldValue('ph_country', value.value)}
                                        onBlur={handleBlur}
                                        maxMenuHeight={175}
                                        placeholder={'Country'}
                                        options={countries}
                                        styles={getSelectStyles()}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group" title="Postal Code">
                                <input
                                    type="text"
                                    className={'form-control' + (errors.ph_zip && touched.ph_zip ? ' is-invalid' : '')}
                                    name="ph_zip"
                                    value={values.ph_zip}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder={'PostalCode'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <div className="form-group" title="Mobile">
                                <LabelMedium>
                                    Mobile Number<Required>*</Required>
                                </LabelMedium>
                                <CopyableField textToCopy={values.mobile}>
                                    <input
                                        type="text"
                                        className={
                                            'form-control' +
                                            (formik.errors.mobile && formik.touched.mobile ? ' is-invalid' : '')
                                        }
                                        name="mobile"
                                        placeholder="+27"
                                        value={formik.values.mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </CopyableField>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group" title="Alternative Contact Number">
                                <LabelMedium>
                                    Alternate Number<Required>*</Required>
                                </LabelMedium>
                                <CopyableField textToCopy={values.a_phone}>
                                    <input
                                        type="text"
                                        className={
                                            'form-control' +
                                            (formik.errors.a_phone && formik.touched.a_phone ? ' is-invalid' : '')
                                        }
                                        name="a_phone"
                                        title="Alternate Number"
                                        placeholder="+27"
                                        value={formik.values.a_phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </CopyableField>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="form-group" title="Landline Number">
                                <LabelMedium>Landline </LabelMedium>
                                <CopyableField textToCopy={values.phone}>
                                    <input
                                        type="text"
                                        className={
                                            'form-control' +
                                            (formik.errors.phone && formik.touched.phone ? ' is-invalid' : '')
                                        }
                                        name="landline"
                                        title="Landline"
                                        placeholder="+27"
                                        value={formik.values.phone}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </CopyableField>
                            </div>
                        </div>
                    </div>
                </DoubleCollapsibleSection>
                <CollapsibleSection title="Emergency Contact and Medical Aid" storageKey="emergencyContact" defaultOpen>
                    <EmergencyContactFields formik={formik} />
                </CollapsibleSection>

                <CollapsibleSection title="Login Details" storageKey="loginDetails" defaultOpen>
                    <div className="card-body-inr">
                        <LoginDetails />
                    </div>
                </CollapsibleSection>
                <CollapsibleSection title="Documents" storageKey="documents" defaultOpen>
                    <div
                        className="col-md-12"
                        style={{
                            margin: '-24px',
                            padding: 0,
                        }}
                    >
                        <ProfilePersonalDocuments docType={'personal_doc'} />
                    </div>
                </CollapsibleSection>

                <div className="form-group d-flex form-group-save-cancel gap-2">
                    <>
                        <StandardButton type="submit" title="Save" disabled={!formik.dirty || loading}>
                            {loading ? <i className="fas fa-cog fa-spin"></i> : <i className="fal fa-save"></i>}
                            Save Changes
                        </StandardButton>

                        <StandardButton
                            variant="danger"
                            type="button"
                            onClick={() => {
                                formik.resetForm();
                            }}
                            title="Cancel"
                        >
                            <i className="fal fa-times"></i>
                            Cancel
                        </StandardButton>
                    </>
                </div>
                {Object.keys(formik.errors).map((key) => (
                    <StandardErrorMessage key={key} formik={formik} name={key} />
                ))}
            </form>
        </>
    );
};

export default PersonalInformation;
